/**
 * Article body editor – web: TinyMCE rich text (self-hosted via jsDelivr CDN, no API key).
 */
import {useEffect, useRef, useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'

const TINYMCE_BASE = 'https://cdn.jsdelivr.net/npm/tinymce@6.8.4'
const TINYMCE_CDN = `${TINYMCE_BASE}/tinymce.min.js`
const EDITOR_ID = 'article-body-editor'

declare global {
  interface Window {
    tinymce?: {
      init: (config: Record<string, unknown>) => Promise<unknown[]>
      remove: (selector: string) => void
      get: (id: string) => {
        mode: {set: (m: string) => void}
        getContent: () => string
        setContent: (v: string) => void
      } | undefined
    }
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('No document'))
      return
    }
    const existing = document.querySelector(
      `script[src="${src}"]`,
    ) as HTMLScriptElement | null
    if (existing) {
      if (window.tinymce) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () =>
          reject(new Error(`Failed to load ${src}`)),
        )
      }
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}

type Props = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  editable?: boolean
}

export function ArticleBodyEditor({
  value,
  onChange,
  placeholder,
  editable = true,
}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const onChangeRef = useRef(onChange)
  const valueRef = useRef(value)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [scriptFailed, setScriptFailed] = useState(false)
  const [editorReady, setEditorReady] = useState(false)
  onChangeRef.current = onChange
  valueRef.current = value

  const isDark =
    (t.palette as Record<string, string>).contrast_0 === '#ffffff' ||
    (t.palette as Record<string, string>).background === '#000'

  useEffect(() => {
    loadScript(TINYMCE_CDN)
      .then(() => setScriptLoaded(true))
      .catch(() => {
        setScriptLoaded(true)
        setScriptFailed(true)
      })
  }, [])

  const placeholderText = placeholder ?? _(msg`Start writing`)

  useEffect(() => {
    if (!scriptLoaded || !window.tinymce || scriptFailed) return

    window.tinymce.init({
      selector: `#${EDITOR_ID}`,
      base_url: TINYMCE_BASE,
      suffix: '.min',
      init_instance_callback: (editor: {
        id: string
        getContent: () => string
        setContent: (v: string) => void
        on: (e: string, fn: () => void) => void
      }) => {
        editor.setContent(valueRef.current || '')
        editor.on('change keyup', () => {
          onChangeRef.current(editor.getContent())
        })
        setEditorReady(true)
      },
      content_style: isDark
        ? 'body { background: #1a1a1a; color: #e7e9ea; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 16px; }'
        : 'body { background: #fff; color: #0f1419; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 16px; }',
      skin: isDark ? 'oxide-dark' : 'oxide',
      content_css: isDark ? 'dark' : 'default',
      menubar: false,
      toolbar:
        'undo redo | blocks | bold italic underline strikethrough | link | bullist numlist | blockquote',
      plugins: 'lists link',
      block_formats: 'Paragraph=p; Heading 2=h2; Heading 3=h3',
      height: 400,
      min_height: 280,
      placeholder: placeholderText,
      branding: false,
      promotion: false,
      resize: true,
      readonly: !editable,
      license_key: 'gpl',
    })

    return () => {
      setEditorReady(false)
      try {
        window.tinymce?.remove(`#${EDITOR_ID}`)
      } catch {}
    }
  }, [scriptLoaded, scriptFailed, isDark, placeholderText, editable])

  useEffect(() => {
    if (!editorReady || !window.tinymce) return
    const ed = window.tinymce.get(EDITOR_ID)
    if (ed && value !== undefined) {
      const current = ed.getContent()
      if (current !== value) ed.setContent(value || '')
    }
  }, [value, editorReady])

  useEffect(() => {
    if (!editorReady || !window.tinymce) return
    const ed = window.tinymce.get(EDITOR_ID)
    if (ed?.mode) ed.mode.set(editable ? 'design' : 'readonly')
  }, [editable, editorReady])

  if (scriptFailed) {
    return (
      <View style={[a.flex_1, {minHeight: 280}]}>
        <textarea
          id={EDITOR_ID}
          defaultValue={value}
          onChange={e =>
            onChangeRef.current((e.target as HTMLTextAreaElement).value)
          }
          style={{
            minHeight: 280,
            width: '100%',
            backgroundColor: isDark ? '#1a1a1a' : '#fff',
            color: isDark ? '#e7e9ea' : '#0f1419',
            border: `1px solid ${isDark ? '#333' : '#ccc'}`,
            borderRadius: 8,
            padding: 12,
            fontSize: 16,
            fontFamily: 'inherit',
            resize: 'vertical' as const,
          }}
          placeholder={placeholderText}
          data-testid="article-body-editor"
        />
      </View>
    )
  }

  return (
    <View style={[a.flex_1, {minHeight: 280}]}>
      <textarea
        id={EDITOR_ID}
        defaultValue=""
        style={{
          minHeight: 280,
          width: '100%',
          visibility: editorReady ? 'hidden' : 'visible',
          position: editorReady ? 'absolute' : 'relative',
          backgroundColor: isDark ? '#1a1a1a' : '#fff',
          color: isDark ? '#e7e9ea' : '#0f1419',
          border: `1px solid ${isDark ? '#333' : '#ccc'}`,
          borderRadius: 8,
          padding: 12,
          fontSize: 16,
          fontFamily: 'inherit',
          resize: 'vertical' as const,
        }}
        placeholder={placeholderText}
        data-testid="article-body-editor"
      />
    </View>
  )
}
